import { Link } from "react-router-dom";
import { Heart, MapPin, Bed, Ruler, Star } from "@phosphor-icons/react";
import { fileUrl } from "@/lib/api";
import { shortGNF } from "@/lib/format";
import { ShareMenuButton, listingShare } from "@/components/ShareListing";

const prefetchDetail = () => import("@/pages/ListingDetailPage");

const categoryLabels = {
  maison: "Maison",
  appartement: "Appartement",
  terrain: "Terrain",
  bureau: "Bureau",
  commercial: "Local commercial",
  chantier: "Chantier",
};

export default function PropertyCard({ listing, isFavorite, onToggleFavorite }) {
  const cover = listing.photos?.[0];
  return (
    <Link
      to={`/annonce/${listing.listing_id}`}
      onMouseEnter={prefetchDetail}
      onTouchStart={prefetchDetail}
      data-testid={`property-card-${listing.listing_id}`}
      className="card-motion group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {cover ? (
          <img
            src={fileUrl(cover, 640)}
            alt={listing.title}
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            Aucune photo
          </div>
        )}

        <div className="absolute left-3 top-3 flex items-center gap-2">
          <span className="rounded-full bg-background/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-foreground shadow-sm">
            {categoryLabels[listing.category] || listing.category}
          </span>
          {listing.featured && (
            <span className="flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-primary-foreground">
              <Star size={12} weight="fill" /> À la une
            </span>
          )}
        </div>

        {onToggleFavorite && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onToggleFavorite(listing.listing_id);
            }}
            data-testid={`favorite-toggle-${listing.listing_id}`}
            className="btn-pill absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-background/95 text-foreground shadow-sm hover:bg-primary hover:text-primary-foreground"
            aria-label="Favori"
          >
            <Heart size={18} weight={isFavorite ? "fill" : "regular"} className={isFavorite ? "text-primary" : ""} />
          </button>
        )}

        <ShareMenuButton
          share={listingShare(listing)}
          testid={`card-share-${listing.listing_id}`}
          className={`btn-pill absolute right-3 ${onToggleFavorite ? "top-14" : "top-3"} flex h-9 w-9 items-center justify-center rounded-full bg-background/95 text-foreground shadow-sm hover:bg-secondary hover:text-secondary-foreground`}
        />

        <div className="absolute bottom-3 left-3 rounded-full bg-secondary px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-secondary-foreground">
          {listing.transaction_type === "location" ? "À louer" : "À vendre"}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <div className="font-display text-2xl font-black leading-tight text-primary">
            {shortGNF(listing.price)}
          </div>
          <h3 className="mt-1 line-clamp-1 font-display text-lg font-semibold">{listing.title}</h3>
        </div>

        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin size={14} weight="duotone" />
          <span className="truncate">
            {listing.quartier}, {listing.city}
          </span>
        </div>

        <div className="mt-auto flex items-center gap-4 border-t border-border pt-3 text-xs font-semibold text-muted-foreground">
          {listing.chambres != null && (
            <span className="flex items-center gap-1">
              <Bed size={14} weight="duotone" /> {listing.chambres} ch.
            </span>
          )}
          {listing.superficie != null && (
            <span className="flex items-center gap-1">
              <Ruler size={14} weight="duotone" /> {listing.superficie} m²
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
